import { ErrorHandler, Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { visitante } from '../http/visitante.interceptor';

export type TipoError = 'JS' | 'CARGA' | 'API' | 'RED';

export interface ErrorReportado {
  tipo: TipoError;
  mensaje: string;
  api?: string | null;
  estado_http?: number | null;
  stack?: string | null;
  detalle?: string | null;
}

const CLAVE_RECARGA = 'vitrina.recarga-version';
const MAX_POR_PAGINA = 30;

// La versión publicada: el nombre del archivo principal cambia en cada deploy (main-ABC123.js)
function version(): string | null {
  const s = document.querySelector<HTMLScriptElement>('script[src*="main"]');
  return s?.src.split('/').pop() ?? null;
}

/** Falló la descarga de una parte de la app: casi siempre porque se publicó una versión nueva */
export function esErrorDeCarga(error: unknown): boolean {
  const m = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading chunk [\w-]+ failed/i.test(m);
}

/**
 * Se publicó una versión nueva con la app abierta: se recarga una vez (a `destino` si se sabe
 * a dónde iba) para traer la nueva. Si ya se recargó hace poco, no insiste.
 */
export function recargarPorVersionNueva(destino?: string): boolean {
  try {
    const antes = Number(sessionStorage.getItem(CLAVE_RECARGA) ?? 0);
    if (Date.now() - antes < 2 * 60_000) return false;
    sessionStorage.setItem(CLAVE_RECARGA, String(Date.now()));
  } catch {
    return false;
  }
  if (destino) location.assign(destino);
  else location.reload();
  return true;
}

/**
 * D-159: manda los errores de la app al registro del dueño (los clientes y vendedores no los
 * reportan). Agrupa los de un mismo momento en un solo envío y no repite el mismo error en la
 * misma página. Usa fetch directo (no HttpClient) para no entrar en los interceptores.
 */
@Injectable({ providedIn: 'root' })
export class ReporteErroresService {
  private readonly auth = inject(AuthService);
  private readonly vistos = new Set<string>();
  private cola: (ErrorReportado & { ruta: string; url: string; version: string | null })[] = [];
  private temporizador: ReturnType<typeof setTimeout> | null = null;

  reportar(e: ErrorReportado): void {
    const clave = `${e.tipo}|${e.api ?? ''}|${e.mensaje.slice(0, 200)}`;
    if (this.vistos.has(clave) || this.vistos.size >= MAX_POR_PAGINA) return;
    this.vistos.add(clave);
    this.cola.push({
      ...e,
      mensaje: e.mensaje.slice(0, 2000),
      stack: e.stack?.slice(0, 8000) ?? null,
      ruta: location.pathname,
      url: location.href.slice(0, 1000),
      version: version(),
    });
    this.temporizador ??= setTimeout(() => this.enviar(), 1500);
  }

  // También al salir de la página: keepalive deja terminar el envío
  enviar(): void {
    this.temporizador = null;
    const lote = this.cola.splice(0, 10);
    if (!lote.length) return;
    const token = this.auth.accessToken();
    fetch(`${environment.apiUrl}/public/errores`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'X-Visitante': visitante(), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ errores: lote }),
    }).catch(() => undefined);
    if (this.cola.length) this.temporizador = setTimeout(() => this.enviar(), 1500);
  }
}

/** Los errores que nadie atrapó: a la consola y al registro; si es una versión vieja, recarga */
@Injectable()
export class ManejadorErrores extends ErrorHandler {
  private readonly reporte = inject(ReporteErroresService);

  override handleError(error: unknown): void {
    super.handleError(error);
    const e = desenvolver(error);
    // Los errores HTTP ya los reporta el interceptor
    if (e && typeof e === 'object' && 'status' in e && 'code' in e) return;
    if (esErrorDeCarga(e)) {
      this.reporte.reportar({ tipo: 'CARGA', mensaje: mensajeDe(e) });
      this.reporte.enviar();
      recargarPorVersionNueva();
      return;
    }
    this.reporte.reportar({ tipo: 'JS', mensaje: mensajeDe(e), stack: e instanceof Error ? (e.stack ?? null) : null });
  }
}

function desenvolver(error: unknown): unknown {
  let e = error as { rejection?: unknown; ngOriginalError?: unknown } | null;
  for (let i = 0; i < 3 && e && typeof e === 'object'; i += 1) {
    const interno = e.rejection ?? e.ngOriginalError;
    if (!interno) break;
    e = interno as typeof e;
  }
  return e;
}

function mensajeDe(e: unknown): string {
  if (e instanceof Error) return `${e.name}: ${e.message}`;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e).slice(0, 500);
  } catch {
    return String(e);
  }
}
